import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard,
  FileSpreadsheet,
  BarChart3,
  Settings,
  TrendingUp,
  Users,
  PieChart,
  Calendar,
  Database,
  Zap,
  Upload,
  DollarSign,
  FileText,
  Activity,
  Plug,
  ChevronDown,
  ChevronRight,
  Target,
  Briefcase,
  LineChart,
  FileBarChart,
  Layers,
  Filter,
  Calculator,
  GitBranch,
  RefreshCw,
} from 'lucide-react';

interface NavItem {
  name: string;
  href?: string;
  icon: any;
  children?: NavItem[];
  badge?: string;
  badgeColor?: string;
}

const navigationSections: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Executive View', href: '/executive', icon: Target },
    ],
  },
  {
    title: 'Financial Planning',
    items: [
      {
        name: 'Modeling',
        icon: FileSpreadsheet,
        children: [
          { name: 'Financial Models', href: '/modeling', icon: FileSpreadsheet },
          { name: 'Scenario Planning', href: '/scenarios', icon: GitBranch },
          { name: 'What-If Analysis', href: '/what-if', icon: Calculator },
        ],
      },
      {
        name: 'Budgeting',
        icon: PieChart,
        children: [
          { name: 'Budget Planning', href: '/budgeting', icon: PieChart },
          { name: 'Budget vs Actual', href: '/budget-actual', icon: BarChart3 },
          { name: 'Variance Analysis', href: '/variance', icon: TrendingUp },
        ],
      },
      {
        name: 'Forecasting',
        icon: TrendingUp,
        children: [
          { name: 'Revenue Forecast', href: '/forecast/revenue', icon: LineChart },
          { name: 'Expense Forecast', href: '/forecast/expense', icon: Activity },
          { name: 'Cash Flow Forecast', href: '/forecast/cashflow', icon: DollarSign },
        ],
      },
    ],
  },
  {
    title: 'Data Management',
    items: [
      {
        name: 'Data Import',
        icon: Upload,
        children: [
          { name: 'File Upload', href: '/import', icon: Upload },
          { name: 'Chart of Accounts', href: '/chart-of-accounts', icon: Layers },
          { name: 'Transactions', href: '/transactions', icon: RefreshCw },
        ],
      },
      {
        name: 'Data Quality',
        icon: Filter,
        children: [
          { name: 'Validation Rules', href: '/validation', icon: Filter },
          { name: 'Data Cleansing', href: '/data-cleanse', icon: Database },
        ],
      },
    ],
  },
  {
    title: 'Financial Statements',
    items: [
      { name: 'P&L Statement', href: '/profit-loss', icon: FileBarChart },
      { name: 'Balance Sheet', href: '/balance-sheet', icon: FileText },
      { name: 'Cash Flow', href: '/cash-flow', icon: DollarSign },
      { name: 'All Reports', href: '/reports', icon: BarChart3 },
    ],
  },
  {
    title: 'Collaboration',
    items: [
      { name: 'Team', href: '/team', icon: Users },
      { name: 'Workflow', href: '/workflow', icon: Zap },
      { name: 'Consolidation', href: '/consolidation', icon: Database },
    ],
  },
  {
    title: 'Integrations',
    items: [
      {
        name: 'Accounting',
        icon: Plug,
        children: [
          { name: 'QuickBooks', href: '/integrations/quickbooks', icon: Briefcase, badge: 'Beta', badgeColor: 'bg-primary-100 text-primary-800' },
          { name: 'Xero', href: '/integrations/xero', icon: Briefcase, badge: 'Coming', badgeColor: 'bg-neutral-100 text-neutral-600' },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      {
        name: 'Organization',
        icon: Settings,
        children: [
          { name: 'General Settings', href: '/settings', icon: Settings },
          { name: 'Users & Permissions', href: '/settings/users', icon: Users },
          { name: 'Audit Log', href: '/settings/audit', icon: FileText },
        ],
      },
    ],
  },
];

const Sidebar = () => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleItem = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName]
    );
  };

  const renderNavItem = (item: NavItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);

    if (hasChildren) {
      return (
        <div key={item.name} className={level > 0 ? 'ml-4' : ''}>
          <button
            onClick={() => toggleItem(item.name)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.name}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-neutral-500" />
            )}
          </button>

          {isExpanded && (
            <div className="mt-1 ml-6 space-y-1">
              {item.children?.map((child) => renderNavItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    if (item.href) {
      return (
        <NavLink
          key={item.name}
          to={item.href}
          className={({ isActive }) =>
            `flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              level > 0 ? 'ml-4' : ''
            } ${
              isActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-neutral-700 hover:bg-neutral-100'
            }`
          }
        >
          <div className="flex items-center space-x-3">
            <item.icon className="w-4 h-4" />
            <span className={`${level > 0 ? 'text-sm' : 'font-medium'}`}>
              {item.name}
            </span>
          </div>
          {item.badge && (
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                item.badgeColor || 'bg-neutral-100 text-neutral-600'
              }`}
            >
              {item.badge}
            </span>
          )}
        </NavLink>
      );
    }

    return null;
  };

  return (
    <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-neutral-200">
        <h1 className="text-xl font-bold text-primary-500">Finance BPA</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-3 space-y-6">
          {navigationSections.map((section) => (
            <div key={section.title}>
              <h3 className="px-3 mb-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => renderNavItem(item))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-neutral-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-primary-600 font-semibold">JD</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-900">John Doe</p>
            <p className="text-xs text-neutral-500">john@example.com</p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
